import { useMemo, useState } from 'react';
import type { TableSort } from '../../../shared/components/SortableTable';
import { useEventDatasetRowCount, useEventDatasetRows } from './useEventDatasets';
import { STANDARD_COLUMNS, type ColumnType } from '../utils/datasetFormUtils';
import { DEFAULT_SORT, UNFILTERABLE } from '../utils/datasetRowsUtils';
import type {
  DatasetColumn,
  EventDatasetFilter,
  EventDatasetRecord,
  EventDatasetRows,
  QueryEventDatasetRequest,
} from '../../../types/backend';

export type RowsView = QueryEventDatasetRequest['view'];

interface Range {
  /** The preset the span was taken from; undefined for a custom range. */
  preset?: string;
  since: string;
  until: string;
}

interface Span {
  since: string;
  until: string;
}

// The backend's QUERY_DEFAULT_LIMIT, the page size the PO chose: the rows one page shows.
const PAGE_SIZE = 100;
const PRESET_MS: Record<string, number> = { '24h': 24 * 3600_000, '7d': 7 * 24 * 3600_000, '30d': 30 * 24 * 3600_000 };
const DEFAULT_PRESET = '7d';

const spanOf = (preset: string): Range => {
  const until = Date.now();
  return { preset, since: new Date(until - PRESET_MS[preset]).toISOString(), until: new Date(until).toISOString() };
};

export interface DatasetRowsBrowser {
  view: RowsView;
  setView: (view: RowsView) => void;
  range: Range;
  /** The custom range being picked, read only on applyCustom. */
  draft: Span;
  selectPreset: (value: string) => void;
  setDraftSince: (iso: string) => void;
  setDraftUntil: (iso: string) => void;
  applyCustom: () => void;
  filters: EventDatasetFilter[];
  setFilters: (filters: EventDatasetFilter[]) => void;
  sort: TableSort;
  setSort: (sort: TableSort) => void;
  page: number;
  hasNext: boolean;
  /** More rows match, but paging stops here: the operator narrows the read instead. */
  atPagingCap: boolean;
  next: () => void;
  previous: () => void;
  refresh: () => void;
  rows: EventDatasetRows | undefined;
  loading: boolean;
  stale: boolean;
  error: string | null;
  count: () => void;
  matched: number | undefined;
  counting: boolean;
  countError: string | null;
  /** Every column of the dataset, standard ones first, and each column's type. */
  columns: DatasetColumn[];
  types: Map<string, ColumnType>;
  /** The columns a filter or a sort may name: every column but list and json ones. */
  filterable: DatasetColumn[];
}

/**
 * The read state of a dataset's rows (EM-UC-37): the view, time range, filters and sort that make the read, and
 * the page tokens that page it. Any change to the read starts again from page 1.
 */
export function useDatasetRowsBrowser(customerId: string, dataset: EventDatasetRecord): DatasetRowsBrowser {
  const [view, setViewState] = useState<RowsView>('history');
  const [range, setRange] = useState<Range>(() => spanOf(DEFAULT_PRESET));
  const [draft, setDraft] = useState<Span>({ since: '', until: '' });
  const [filters, setFiltersState] = useState<EventDatasetFilter[]>([]);
  const [sort, setSortState] = useState<TableSort>(DEFAULT_SORT);
  // The token of each page read so far; the first page has none. Previous reads the page before again.
  const [pages, setPages] = useState<(string | undefined)[]>([undefined]);
  const [countedRead, setCountedRead] = useState<QueryEventDatasetRequest | null>(null);

  const columns = [...STANDARD_COLUMNS, ...dataset.columns];
  const types = new Map(columns.map((c) => [c.name, c.type]));
  const filterable = columns.filter((c) => !UNFILTERABLE.includes(c.type));

  // The read the view, range and filters name; the count and every page read it. Memoised so its identity
  // changes only with the read: a count is shown only while countedRead is this same object.
  const read = useMemo<QueryEventDatasetRequest>(() => ({
    customer_id: customerId,
    name: dataset.name,
    view,
    ...(view === 'history' && { since: range.since, until: range.until }),
    ...(filters.length > 0 && { filters }),
  }), [customerId, dataset.name, view, range.since, range.until, filters]);

  const page = pages.length;
  const request: QueryEventDatasetRequest = {
    ...read,
    ...((sort.key !== DEFAULT_SORT.key || sort.direction !== DEFAULT_SORT.direction)
      && { order_by: sort.key, descending: sort.direction === 'desc' }),
    limit: PAGE_SIZE,
    ...(pages[page - 1] !== undefined && { next_token: pages[page - 1] }),
  };

  const { rows, loading, stale, error, refetch } = useEventDatasetRows(request);
  // The count belongs to the read it was asked for; a changed read is a new object, so it shows none.
  const counter = useEventDatasetRowCount(read, countedRead === read);

  const firstPage = () => setPages([undefined]);

  return {
    view,
    setView: (next) => {
      setViewState(next);
      firstPage();
    },
    range,
    draft,
    selectPreset: (value) => {
      if (value === 'custom') {
        setDraft({ since: range.since, until: range.until });
        setRange((prev) => ({ ...prev, preset: undefined }));
        return;
      }
      setRange(spanOf(value));
      firstPage();
    },
    setDraftSince: (iso) => setDraft((prev) => ({ ...prev, since: iso })),
    setDraftUntil: (iso) => setDraft((prev) => ({ ...prev, until: iso })),
    applyCustom: () => {
      setRange({ since: draft.since, until: draft.until });
      firstPage();
    },
    filters,
    setFilters: (next) => {
      setFiltersState(next);
      firstPage();
    },
    sort,
    setSort: (next) => {
      setSortState(next);
      firstPage();
    },
    page,
    hasNext: !!rows?.next_token,
    atPagingCap: !!rows?.paging_capped,
    next: () => {
      const token = rows?.next_token;
      if (token) setPages((prev) => [...prev, token]);
    },
    previous: () => setPages((prev) => (prev.length > 1 ? prev.slice(0, -1) : prev)),
    refresh: () => {
      // A preset range ends now, so a refresh reads up to the new now.
      if (view === 'history' && range.preset) setRange(spanOf(range.preset));
      else if (page === 1) refetch();
      firstPage();
    },
    rows,
    loading,
    stale,
    error,
    // A second click on the same read counts it again, so a failed count can be retried.
    count: () => (countedRead === read ? counter.refetch() : setCountedRead(read)),
    matched: countedRead === read ? counter.matched : undefined,
    counting: counter.counting,
    countError: countedRead === read ? counter.error : null,
    columns,
    types,
    filterable,
  };
}
